import React from 'react';
import EntityAvatar from 'components/Entities/EntityAvatar';
import EntityActions from './EntityActions';
import EntitySubscription from '../../ui/EntitySubscription';
import type { EntityAvatarCrosspostData, EntityAvatarEntityType } from "components/Entities/EntityAvatar/entityAvatar.types";
import { Action, EntityType, Feedback, Impact, Issue, Solution, Collection } from 'util/constants';

type SubscriptionSupportedEntityType = React.ComponentProps<typeof EntitySubscription>["entityType"];
type CompactCardEntity = Issue | Action | Feedback | Impact | Solution | Collection;

interface EntityCompactCardProps {
  entity?: CompactCardEntity;
  displayEntity?: CompactCardEntity; // The entity to display (subject for influencers, topic for influenced)
  entityId?: number;
  entityType: EntityType;
  showMeta?: boolean;
  showActions?: boolean;
  onClick?: () => void;
  compactCard?: boolean;
  // Crosspost data when entityType is 'crosspost'
  crosspostData?: EntityAvatarCrosspostData;
  entityChip?: boolean;
  isInputField?: boolean;
  isEntitySection?: boolean;
  isEntityProfilePage?: boolean;
  isGeneralEntity?: boolean;
  showCloseChip?: boolean;
  showLink?: boolean;
  showSubscription?: boolean;
  showVote?: boolean;
  className?: string;
}

const EntityCompactCard: React.FC<EntityCompactCardProps> = ({
  entity,
  displayEntity,
  entityId,
  entityType = 'issue',
  showMeta = true,
  showActions = true,
  showVote = true,
  onClick,
  crosspostData,
  compactCard = false,
  entityChip = true,
  isInputField = false,
  isEntitySection = false,
  isEntityProfilePage = false,
  isGeneralEntity = true,
  showCloseChip = false,
  showLink = true,
  showSubscription = false,
  className = '',
}) => {
  const subscriptionEntity = displayEntity ?? entity;
  const avatarRowClassName = "flex w-full items-center text-left";
  
  const handleCardClick = (e: React.MouseEvent<HTMLElement>) => {
    // Only trigger onClick if we're not clicking on actions or links
    const target = e.target instanceof HTMLElement ? e.target : null;
    const isActionClick = target?.closest(".entity-actions, .ignore-card-click, a, button");
    
    if (!isActionClick && onClick) {
      onClick();
    }
  };

  const avatarContent = (
    <EntityAvatar
      entity={displayEntity}
      entityId={entityId}
      entityType={entityType as EntityAvatarEntityType}
      entityChip={entityChip}
      isInputField={isInputField}
      isEntitySection={isEntitySection}
      isEntityProfilePage={isEntityProfilePage}
      isGeneralEntity={isGeneralEntity}
      showCloseChip={showCloseChip}
      showLink={showLink}
      compactCard={compactCard}
      className={className}
      crosspostData={crosspostData}
    />
  );

  return (
    <div 
      className="entity-compact-card p-1 space-y-2 cursor-pointer hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon transition-colors rounded-lg"
      
    >
      {/* Entity Avatar in chip style */}
      {onClick ? (
        <button
          type="button"
          className={avatarRowClassName}
          onClick={handleCardClick}
          aria-label="Open entity card"
        >
          {avatarContent}
        </button>
      ) : (
        <div className={avatarRowClassName}>{avatarContent}</div>
      )}

      {/* Entity Subscription */}
      {showSubscription && subscriptionEntity && (
        <div className="ignore-card-click">
          <EntitySubscription
            entityId={subscriptionEntity.id}
            entityType={entityType as SubscriptionSupportedEntityType}
            variant="mini"
          />
        </div>
      )}

      {/* Entity Actions */}
      {showActions && entity && (
        <div className="ignore-card-click">
          <EntityActions
            entity={entity}
            entityType={entityType as EntityAvatarEntityType}
            showEntitySpecificActions={true}
            showSave={false}
            showShare={false}
            showVote={showVote}
            showComment={false}
          />
        </div>
      )}
    </div>
  );
};

export default EntityCompactCard; 